import { useThree } from '@react-three/fiber';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { BackSide, Mesh, ShaderMaterial, SphereGeometry } from 'three';

import fragmentShader from './sky.frag';
import vertexShader from './sky.vert';

const OBJ_NAME = 'BushfireSky';

export interface BushfireSkyProps {
  radius?: number;
  opacity?: number;
}

export interface BushfireSkyRef {
  opacity: number;
}

const BushfireSky = forwardRef<BushfireSkyRef, BushfireSkyProps>(
  ({ radius = 2000, opacity = 1 }, ref) => {
    const scene = useThree(state => state.scene);
    const meshRef = useRef<Mesh | null>(null);

    useImperativeHandle(ref, () => ({
      get opacity() {
        if (!meshRef.current) return 0;
        const smat = meshRef.current.material as ShaderMaterial;
        return meshRef.current.visible ? smat.uniforms.uOpacity.value : 0;
      },
      set opacity(n: number) {
        if (!meshRef.current) return;
        const smat = meshRef.current.material as ShaderMaterial;
        smat.uniforms.uOpacity.value = n;
        meshRef.current.visible = n > 0;
      },
    }));

    useEffect(() => {
      if (scene.getObjectByName(OBJ_NAME)) return;

      const sm = new ShaderMaterial({
        vertexShader,
        fragmentShader,
        side: BackSide,
        uniforms: {
          uOpacity: { value: opacity },
        },
        transparent: true,
      });
      const geo = new SphereGeometry(radius, 32, 32);

      const mesh = new Mesh(geo, sm);
      mesh.name = OBJ_NAME;
      mesh.renderOrder = -9999;

      meshRef.current = mesh;
      scene.add(mesh);

      return () => {
        scene.remove(mesh);
        meshRef.current = null;
      };
    }, [scene, radius, opacity]);

    return null;
  }
);

BushfireSky.displayName = 'BushfireSky';
export default BushfireSky;
